/**
 * Frontend logger. Routes through native console.* so DevTools level filters
 * still work, but gates debug/info by env.
 *
 * Default thresholds:
 *   prod → info (info + warn + error visible)
 *   dev  → debug (everything)
 *
 * Override per-session via localStorage:
 *   localStorage.setItem('wand:loglevel', 'debug' | 'info' | 'warn' | 'error')
 *
 * Use this instead of console.* directly so the level threshold is global.
 */

import { PUBLIC_WAND_ENV } from '$env/static/public';

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 } as const;
type Level = keyof typeof LEVELS;

function resolveLevel(): number {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('wand:loglevel') as Level | null;
    if (stored && stored in LEVELS) return LEVELS[stored];
  }
  return PUBLIC_WAND_ENV === 'prod' ? LEVELS.info : LEVELS.debug;
}

const threshold = resolveLevel();

export const log = {
  debug: (...args: unknown[]) => {
    if (threshold <= LEVELS.debug) console.debug(...args);
  },
  info: (...args: unknown[]) => {
    if (threshold <= LEVELS.info) console.info(...args);
  },
  warn: (...args: unknown[]) => {
    if (threshold <= LEVELS.warn) console.warn(...args);
  },
  // This wrapper is the canonical log path; reportError uses log.error
  // internally. The `local/no-console-error` rule is turned off for this
  // single file via the override in eslint.config.js so the ban on
  // suppression comments stays clean everywhere else.
  error: (...args: unknown[]) => console.error(...args),
};